import React from "react";
import { Subscription } from "rxjs";
import NavigationTokens from "../../constants/navigationTokens";
import LogoutUrlService from "../../utilities/logoutUrlService";
import MenuItemService from "../../utilities/menuItemService";
import NavigationTokensService from "../../utilities/navigationTokensService";
import UserService from "../../utilities/userService";
import Icon from "../icon/icon";
import YourRepresentative, {
  RepTypes,
} from "../yourRepresentative/yourRepresentative";
import "./desktopDropdown.scss";
import MenuItem from "./menuItem";
import MenuItemDivider from "./menuItemDivider";
import LoginUrlService from "../../utilities/loginUrlService";
import { WBMUrlService } from "@wbmason/url-service";
import ConfigProps from "../../constants/configProps";
import ConfigPropService from "../../utilities/configPropService";
import EnvironmentService from "../../utilities/environmentService";
import { BaseHostUrlService } from "../../utilities/baseHostUrlService";

export interface IDesktopDropdownProps {
  isLoggedIn: boolean;
  hasMultipleAccounts: boolean;
  className?: string;
  menuId?: string;
  style?: React.CSSProperties;
  onMouseEnter: React.MouseEventHandler<HTMLDivElement>;
  onMouseLeave: React.MouseEventHandler<HTMLDivElement>;
  onlineInvoicingSignUpUrl: boolean;
  oiSignup: string;
  accountStatement: string;
}

export interface IDesktopDropdownState {
  invoicesAndPaymentsHref: string | undefined;
  logoutHref: string;
  myAccountAndSettingsHref: string;
  displaySignOut: boolean;
  displayAccountCenterDashboard: boolean;
  displayAccountAndSettingsLink: boolean;
  switchAccountHref: string;
  contactUsHref: string;
  oiState: string;
}

class DesktopDropdown extends React.Component<
  IDesktopDropdownProps,
  IDesktopDropdownState
> {
  subsToUnsubscribe: Subscription[] = [];

  constructor(props: IDesktopDropdownProps) {
    super(props);

    this.state = {
      invoicesAndPaymentsHref: undefined,
      logoutHref: "",
      myAccountAndSettingsHref: "",
      displaySignOut: false,
      displayAccountCenterDashboard: false,
      switchAccountHref: "",
      contactUsHref: "",
      oiState: "",
      displayAccountAndSettingsLink: false,
    };
  }

  componentDidMount() {
    const switchAccountUrl = LoginUrlService.getPreLoadedSwitchAccountUrl();
    this.subsToUnsubscribe.push(
      NavigationTokensService.get([
        NavigationTokens.accountStatement,
        NavigationTokens.myAccount,
        NavigationTokens.contactUs,
        NavigationTokens.OISignup,
      ]).subscribe((navTokens) => {
        this.subsToUnsubscribe.push(
          ConfigPropService.get([
            ConfigProps.EnableResponsiveAccountCenter,
          ]).subscribe((configProps: Record<string, string>) => {
            const enableResponsiveAccountCenter = ConfigPropService.isYesOrTrue(
              configProps[ConfigProps.EnableResponsiveAccountCenter]
            );

            const responsiveAccountCenterUrl =
              WBMUrlService.getAccountCenterUrl(
                BaseHostUrlService.get(),
                EnvironmentService.get().ecomVnextUrl,
                true
              );

            this.setState({
              myAccountAndSettingsHref: enableResponsiveAccountCenter
                ? responsiveAccountCenterUrl
                : navTokens[NavigationTokens.myAccount],
              contactUsHref: navTokens[NavigationTokens.contactUs],
              switchAccountHref: switchAccountUrl,
            });
          })
        );
      })
    );

    this.subsToUnsubscribe.push(
      MenuItemService.displaySignOut().subscribe((value) => {
        this.setState({
          displaySignOut: value,
        });
      })
    );

    this.subsToUnsubscribe.push(
      MenuItemService.displayAccountCenterDashboard().subscribe((value) => {
        this.setState({
          displayAccountCenterDashboard: value,
        });
      })
    );

    this.subsToUnsubscribe.push(
      MenuItemService.displayAccountAndSettingsLink().subscribe((value) => {
        this.setState({
          displayAccountAndSettingsLink: value,
        });
      })
    );

    this.subsToUnsubscribe.push(
      LogoutUrlService.get().subscribe((value) => {
        this.setState({
          logoutHref: value,
        });
      })
    );
  }

  componentWillUnmount() {
    for (const sub of this.subsToUnsubscribe) {
      sub.unsubscribe();
    }
  }

  static getDerivedStateFromProps(
    nextProps: IDesktopDropdownProps,
    prevState: IDesktopDropdownState
  ) {
    const oiState = [
      nextProps.accountStatement,
      nextProps.oiSignup,
      nextProps.onlineInvoicingSignUpUrl,
    ].join(",");

    if (oiState !== prevState.oiState) {
      return {
        invoicesAndPaymentsHref: nextProps.onlineInvoicingSignUpUrl
          ? nextProps.oiSignup
          : nextProps.accountStatement,
        oiState: oiState,
      };
    }

    return null;
  }

  renderMenu(menuId: string | undefined) {
    return (
      <>
        {UserService.displayUserInfo() && (
          <>
            {this.state.displayAccountAndSettingsLink && (
              <>
                <MenuItem
                  label="My Account & Settings"
                  svg={() => {
                    return <Icon>account_circle</Icon>;
                  }}
                  linkClass={this.state.myAccountAndSettingsHref ? "" : "inactive"}
                  href={this.state.myAccountAndSettingsHref}
                />
                <MenuItemDivider />
              </>
            )}        

            {this.state.displayAccountCenterDashboard && (
              <MenuItem icon="dashboard" label="Account Center Dashboard" />
            )}

            {this.state.invoicesAndPaymentsHref && (
              <>
                <MenuItem
                  label="Invoices & Payments"
                  icon="attach_money"
                  linkClass={
                    this.state.invoicesAndPaymentsHref ? "" : "inactive"
                  }
                  href={this.state.invoicesAndPaymentsHref}
                />

                <MenuItemDivider />
              </>
            )}

            <MenuItem
              label="Customer Support"
              icon="contact_support"
              linkClass={this.state.contactUsHref ? "" : "inactive"}
              href={this.state.contactUsHref}
            />

            <YourRepresentative
              repType={RepTypes.customerService}
              isDesktop={true}
            />
          </>
        )}

        {this.props.isLoggedIn && this.state.displaySignOut && (
          <>
            {UserService.displayUserInfo() && <MenuItemDivider />}
            {this.props.hasMultipleAccounts && (
              <a
                className={`btn-block text-center menu-item-content ${
                  this.state.switchAccountHref ? "active" : ""
                }`}
                href={this.state.switchAccountHref}
              >
                <span className="float-left mb-3 switch-account-wrapper">
                  <Icon className="switch-account align-top menu-item-icon mr-3">
                    swap_horizontal_circle
                  </Icon>
                  <span>Switch Account</span>
                </span>
              </a>
            )}
            <a
              className={this.state.logoutHref ? "" : "inactive"}
              href={this.state.logoutHref}
            >
              <button className="btn-block sign-out menu-item-content">
                <span className="float-left">
                  <Icon className="align-top menu-item-icon mr-3">
                    exit_to_app
                  </Icon>
                  <span className="menu-item-label">Sign Out</span>
                </span>
              </button>
            </a>
          </>
        )}
      </>
    );
  }

  render() {
    return (
      <div
        className={`desktop-dropdown ${this.props.className}`}
        onMouseEnter={this.props.onMouseEnter}
        onMouseLeave={this.props.onMouseLeave}
        style={this.props.style}
      >
        <div className="container menu-items">
          {this.renderMenu(this.props.menuId)}
        </div>
      </div>
    );
  }
}

export default DesktopDropdown;
